"use client";
import { UsersDetailsSkeletons } from "../../components/skeletons/users-skeletons";
import { Breadcrumb, Button, Select, Spin } from "antd";
import { ArrowLeft, File } from "lucide-react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";

import {
  useUpdateConsultationStatusServiceUpdate,
  useConsultationDetail,
} from "../../../hooks/api/consultation-bookings/hooks";
import { cn } from "../../../lib/utils";
import { IConsultationBookingPayloadType } from "../../../types/api/consultation-book-types";
import { useMessageApi } from "../../../provider/AntdNotification";

const STATUS_OPTIONS = [
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Cancelled", value: "CANCELLED" },
  { label: "Completed", value: "COMPLETED" },
];

const ConsultationDetails = () => {
  const messageApi = useMessageApi();
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: userDetail, isLoading } = useConsultationDetail(id ?? "");

  const currentStatus = userDetail?.data?.status ?? "UPCOMING";

  const { mutate: updateStatus, isPending } =
    useUpdateConsultationStatusServiceUpdate({
      onSuccess: () => {
        messageApi.success("Status updated successfully");
      },
      onError: (error: any) => {
        messageApi.error(error.message);
      },
    });

  return (
    <div className="w-full">
      {/* TOP HEADER */}
      <div className="mb-6">
        <div className="flex justify-start w-full">
          <Button
            className="flex items-center gap-2 mb-2 cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} className="text-primary" />
            <span className="text-primary font-semibold">Back</span>
          </Button>
        </div>

        <h1 className="text-lg sm:text-3xl font-semibold sm:mb-1">
          Consultation Details
        </h1>

        <Breadcrumb
          separator=">"
          items={[
            { title: "Dashboard" },
            { title: "Consultation" },
            { title: "Consultation Details" },
          ]}
          className="text-xs sm:text-base"
        />
      </div>
      <div className="my-5 justify-end flex gap-4 items-center">
        <Select
          value={currentStatus}
          style={{ width: 260 }}
          loading={isPending}
          options={STATUS_OPTIONS}
          onChange={(value) => {
            if (!id) return;
            updateStatus({
              _id: id,
              status: value,
            } as IConsultationBookingPayloadType);
          }}
        />

        <span
          className={cn(
            "px-3 py-1 rounded-full text-xs font-semibold capitalize",
            currentStatus === "PENDING" && "bg-blue-100 text-blue-700",
            currentStatus === "APPROVED" && "bg-green-100 text-green-700",

            currentStatus === "CANCELLED" && "bg-red-200 text-red-700",
            currentStatus === "COMPLETE" && "bg-green-200 text-green-700",
          )}
        >
          Current: {isPending ? <Spin /> : currentStatus.replaceAll("_", " ")}
        </span>
      </div>
      {/* GRID LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT DETAILS */}
        <div className="space-y-6">
          {isLoading ? (
            <UsersDetailsSkeletons rows={4} />
          ) : (
            <SectionCard title="Consultation Details">
              <TwoCol
                label="Consultation ID"
                value={userDetail?.data?.booking_id ?? ""}
              />
              <TwoCol label="User Name" value={userDetail?.data?.name ?? ""} />
              <TwoCol label="Purpose" value={userDetail?.data?.purpose ?? ""} />
              <TwoCol
                label="Schedule Date & Time"
                value={`${moment(userDetail?.data?.date).format(
                  "D MMM, YYYY",
                )} , ${moment(userDetail?.data?.start_time).format("hh:mm A")}`}
              />
              <div>
                <p className="text-sm text-primary mb-1">Document</p>

                <a
                  href={userDetail?.data?.doc_file?.[0] || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-blue-600 hover:underline"
                >
                  <File />
                  <span>View Document</span>
                </a>
              </div>
            </SectionCard>
          )}
        </div>

        {/* RIGHT USER DETAILS */}
        <div className="space-y-6 col-span-1 lg:col-span-1">
          {isLoading ? (
            <UsersDetailsSkeletons rows={2} />
          ) : (
            <SectionCard title="User Details " className="space-y-2">
              <TwoCol label="Name" value={userDetail?.data?.user?.name ?? ""} />
              <TwoCol
                label="Email"
                value={userDetail?.data?.user?.email ?? "N/A"}
              />
              <TwoCol
                label="Phone Number"
                value={userDetail?.data?.user?.phone ?? ""}
              />
              <TwoCol label="Filling Year" value={""} />
            </SectionCard>
          )}
        </div>
      </div>
    </div>
  );
};

// ------------------------------------------------------------------------
// COMPONENTS
// ------------------------------------------------------------------------

const SectionCard = ({
  title,
  link,
  children,
  className,
}: {
  title: string;
  link?: string;
  children: any;
  className?: string;
}) => (
  <div
    className={cn(
      "p-5 rounded-xl bg-[#FFEFE5] border border-[#F8D2BF]",
      className,
    )}
  >
    <div className="flex justify-between mb-3 border-b-primary border-b pb-1 w-full">
      <h3 className="font-semibold text-lg text-primary ">{title}</h3>
      {link && <span className="text-primary cursor-pointer">{link}</span>}
    </div>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>
  </div>
);

const TwoCol = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => (
  <div>
    <p className="text-sm text-primary mb-1">{label}</p>
    <p className="text-sm font-medium text-gray-800">{value}</p>
  </div>
);

export default ConsultationDetails;
